import { useEffect, useState } from 'react';
import api from '../services/api';
import { isZohoGateEnabled, setZohoGateEnabled } from '../contexts/ZohoSessionContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  ShieldCheck,
  KeyRound,
  Save,
  RefreshCw,
  Check,
  AlertTriangle,
  Lock,
  Loader2,
  X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  ZOHO_CLIENT_ID:     'Client ID',
  ZOHO_CLIENT_SECRET: 'Client Secret',
  ZOHO_REFRESH_TOKEN: 'Refresh Token',
  ZOHO_ACCOUNTS_URL:  'Accounts URL',
  ZOHO_API_DOMAIN:    'API Domain',
  ZOHO_CRM_ORG:       'CRM Org',
};

const FIELD_HINTS = {
  ZOHO_CLIENT_ID:     'OAuth 2.0 Client ID from your Zoho API Console',
  ZOHO_CLIENT_SECRET: 'OAuth 2.0 Client Secret — keep this private',
  ZOHO_REFRESH_TOKEN: 'Long-lived refresh token for server-side API access',
  ZOHO_ACCOUNTS_URL:  'e.g. https://accounts.zoho.eu',
  ZOHO_API_DOMAIN:    'e.g. https://www.zohoapis.eu',
  ZOHO_CRM_ORG:       'Your Zoho CRM organisation ID',
};

/**
 * Super-admin only UI for managing Zoho CRM credentials.
 * Write-only pattern — plaintext credentials NEVER leave the backend.
 * The frontend only ever sees "Set ✓ / Not set ✗" status per field.
 */
export const ZohoIntegrationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState({});
  const [meta, setMeta] = useState({ updated_at: null, updated_by_email: null });

  // Step-up
  const [showStepUp, setShowStepUp] = useState(false);
  const [stepUpPassword, setStepUpPassword] = useState('');
  const [stepUpCode, setStepUpCode] = useState('');
  const [stepUpLoading, setStepUpLoading] = useState(false);
  const [stepUpToken, setStepUpToken] = useState(null);

  // Edit form (write-only — inputs start empty every time)
  const [editing, setEditing] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/admin/integrations/zoho`);
      setStatuses(res.data.statuses || {});
      setMeta({ updated_at: res.data.updated_at, updated_by_email: res.data.updated_by_email });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Zoho status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const openStepUp = () => setShowStepUp(true);

  const submitStepUp = async () => {
    if (!stepUpPassword.trim()) return toast.error('Password is required');
    setStepUpLoading(true);
    try {
      const res = await api.post(`${API}/admin/integrations/zoho/verify-step-up`, {
        password: stepUpPassword,
        totp_code: stepUpCode || '',
      });
      setStepUpToken(res.data.step_up_token);
      setShowStepUp(false);
      setEditing(true);
      setEditValues({});
      setTestResult(null);
      toast.success('Verified — edit mode unlocked for 5 minutes');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    } finally {
      setStepUpLoading(false);
      setStepUpPassword('');
      setStepUpCode('');
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditValues({});
    setStepUpToken(null);
    setTestResult(null);
  };

  const onTest = async () => {
    if (!stepUpToken) {
      toast.error('Re-verify to test');
      return openStepUp();
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post(`${API}/admin/integrations/zoho/test`, {
        step_up_token: stepUpToken,
        credentials: editValues,  // blank fields fall back to stored values on backend
      });
      setTestResult(res.data);
      setStepUpToken(null);
      if (res.data.success) toast.success(`Valid — ${res.data.scopes.length} scopes granted`);
      else toast.error('Zoho rejected these credentials');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
      setStepUpToken(null);
    } finally {
      setTesting(false);
    }
  };

  const onSave = async () => {
    if (!stepUpToken) {
      toast.error('Re-verify to save');
      return openStepUp();
    }
    const anyFilled = Object.values(editValues).some(v => v?.trim());
    if (!anyFilled) return toast.error('Enter at least one field to update');
    setSaving(true);
    try {
      const res = await api.put(`${API}/admin/integrations/zoho`, {
        step_up_token: stepUpToken,
        credentials: editValues,
      });
      toast.success(`Saved — ${res.data.fields_updated.length} field(s) updated`);
      setStepUpToken(null);
      setEditing(false);
      setEditValues({});
      setTestResult(null);
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
      setStepUpToken(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Security notice */}
      <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
        <ShieldCheck className="w-6 h-6 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-bold text-amber-900 mb-0.5">Super-admin only · MFA required · Write-only</div>
          <div className="text-amber-800">
            Credentials are encrypted and stored server-side only. They are <strong>never sent to the browser</strong>.
            To update a field, enter the new value and save — leave a field blank to keep its existing value.
            All access is logged.
          </div>
        </div>
      </div>

      {/* Zoho login-check toggle */}
      <ZohoGateToggle />

      {/* Credentials status + edit */}
      <div className="rounded-xl border bg-white p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Zoho CRM Credentials</h3>
            {meta.updated_at && (
              <p className="text-xs text-gray-500 mt-0.5">
                Last updated {new Date(meta.updated_at).toLocaleString('en-GB')}
                {meta.updated_by_email ? ` by ${meta.updated_by_email}` : ''}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm"
              onClick={fetchStatus} disabled={loading}
              data-testid="zoho-settings-refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {!editing && (
              <Button
                size="sm"
                onClick={openStepUp}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="zoho-settings-edit-btn"
              >
                <KeyRound className="w-3.5 h-3.5 mr-1" />
                Edit Credentials
              </Button>
            )}
            {editing && (
              <>
                <Button variant="outline" size="sm" onClick={cancelEdit}>
                  <X className="w-3.5 h-3.5 mr-1" /> Cancel
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={onTest} disabled={testing}
                  data-testid="zoho-settings-test-btn"
                >
                  {testing
                    ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    : <ShieldCheck className="w-3.5 h-3.5 mr-1" />}
                  Test
                </Button>
                <Button
                  size="sm"
                  onClick={onSave} disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="zoho-settings-save-btn"
                >
                  {saving
                    ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    : <Save className="w-3.5 h-3.5 mr-1" />}
                  Save
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Test result banner */}
        {testResult && (
          <div className={`rounded-lg p-3 mb-4 text-sm ${
            testResult.success
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {testResult.success ? (
              <>
                <div className="font-semibold mb-1">
                  <Check className="inline w-4 h-4 mr-1" /> Credentials valid
                </div>
                <div className="text-xs">
                  API domain: {testResult.api_domain} · Expires in: {testResult.expires_in}s
                </div>
                <div className="text-xs mt-1">
                  Scopes ({testResult.scopes.length}): {testResult.scopes.join(', ')}
                </div>
              </>
            ) : (
              <>
                <div className="font-semibold mb-1">
                  <AlertTriangle className="inline w-4 h-4 mr-1" /> Rejected by Zoho
                </div>
                <div className="text-xs break-all">{testResult.error}</div>
              </>
            )}
          </div>
        )}

        {/* Status view (not editing) */}
        {!editing && (
          <div className="divide-y divide-gray-100">
            {Object.entries(FIELD_LABELS).map(([field, label]) => {
              const s = statuses[field] || {};
              return (
                <div key={field} className="flex items-center justify-between py-2.5">
                  <div>
                    <span className="text-sm font-semibold text-gray-800">{label}</span>
                    <span className="text-[11px] text-gray-400 ml-2 font-mono">{field}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {s.is_set ? (
                      <span className="flex items-center gap-1 text-emerald-600 text-xs font-semibold">
                        <Check className="w-3.5 h-3.5" /> Set
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-500 text-xs font-semibold">
                        <AlertTriangle className="w-3.5 h-3.5" /> Not set
                      </span>
                    )}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wide ${
                      s.source === 'db'
                        ? 'bg-emerald-100 text-emerald-700'
                        : s.source === 'env'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-500'
                    }`}>
                      {s.source === 'db' ? 'Encrypted DB' : s.source === 'env' ? '.env' : 'Missing'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Edit form (write-only inputs) */}
        {editing && (
          <div className="space-y-3">
            <p className="text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-2 border border-blue-100">
              <Lock className="inline w-3 h-3 mr-1" />
              Credentials are hidden as you type. Leave a field blank to keep its existing value.
              The backend merges your input with stored values before testing or saving.
            </p>
            {Object.entries(FIELD_LABELS).map(([field, label]) => {
              const s = statuses[field] || {};
              return (
                <div key={field}>
                  <Label className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                    {label}
                    {s.is_set
                      ? <span className="text-emerald-600 font-normal">(currently set)</span>
                      : <span className="text-red-500 font-normal">(not set)</span>}
                  </Label>
                  <p className="text-[11px] text-gray-400 mb-1">{FIELD_HINTS[field]}</p>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    value={editValues[field] || ''}
                    onChange={(e) => setEditValues(p => ({ ...p, [field]: e.target.value }))}
                    placeholder={s.is_set ? 'Leave blank to keep existing value' : `Paste new ${label}`}
                    className="font-mono text-sm"
                    data-testid={`zoho-edit-${field}`}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Step-up Dialog */}
      <Dialog open={showStepUp} onOpenChange={(o) => { if (!o) setShowStepUp(false); }}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              Security step-up required
            </DialogTitle>
            <DialogDescription>
              Re-enter your password and authenticator code to unlock credential editing for 5 minutes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-semibold">Password</Label>
              <Input
                type="password"
                autoFocus
                value={stepUpPassword}
                onChange={(e) => setStepUpPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitStepUp()}
                placeholder="Your account password"
                data-testid="zoho-stepup-password"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">
                Authenticator code <span className="text-gray-400 font-normal">(leave blank if not set up)</span>
              </Label>
              <Input
                inputMode="numeric"
                pattern="\d*"
                maxLength={8}
                value={stepUpCode}
                onChange={(e) => setStepUpCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && submitStepUp()}
                placeholder="6-digit code"
                className="font-mono"
                data-testid="zoho-stepup-totp"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowStepUp(false)}>Cancel</Button>
            <Button
              onClick={submitStepUp}
              disabled={stepUpLoading || !stepUpPassword.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="zoho-stepup-submit"
            >
              {stepUpLoading
                ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Verifying…</>
                : 'Verify'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ZohoIntegrationSettings;

// ---------------------------------------------------------------------------

function ZohoGateToggle() {
  const [enabled, setEnabled] = useState(() => isZohoGateEnabled());
  const handleToggle = () => {
    const next = !enabled;
    setZohoGateEnabled(next);
    setEnabled(next);
    toast.success(next ? 'Zoho login check enabled' : 'Zoho login check disabled');
  };
  return (
    <div className="rounded-xl border bg-white p-4 flex items-start gap-3">
      <Lock className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-bold text-gray-900">Zoho login check (per-browser gate)</h3>
          <button
            type="button"
            onClick={handleToggle}
            role="switch"
            aria-checked={enabled}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
              enabled ? 'bg-emerald-500' : 'bg-gray-300'
            }`}
            data-testid="zoho-gate-toggle"
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
          When ON, users are prompted to sign in to Zoho CRM in another tab before CRM features unlock in this browser.
          Most users don&apos;t need this — group access is already enforced server-side.{' '}
          <strong className={enabled ? 'text-emerald-700' : 'text-gray-700'}>Currently {enabled ? 'ON' : 'OFF'} (default off)</strong>.
        </p>
      </div>
    </div>
  );
}
